'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { getMe as getAuthMe, logout as authLogout } from '@/lib/api/auth';
import type { DashboardUser } from '@/lib/types/dashboard';

const AUTH_TOKEN_KEY = 'meetx_auth_token';

type AuthContextValue = {
  user: DashboardUser | null;
  loading: boolean;
  token: string | null;
  setSession: (token: string, user?: Partial<DashboardUser>) => void;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  token: null,
  setSession: () => undefined,
  signOut: async () => undefined,
});

function toDashboardUser(user: Partial<DashboardUser> & { id?: string | number; name?: string; email?: string }): DashboardUser {
  return {
    id: String(user.id || ''),
    name: user.name || 'PrimaVM User',
    email: user.email || '',
    avatar: user.avatar,
    pmi: user.pmi || '',
    role: user.role || 'participant',
    status: user.status || 'available',
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<DashboardUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  function setSession(nextToken: string, nextUser?: Partial<DashboardUser>) {
    localStorage.setItem(AUTH_TOKEN_KEY, nextToken);
    setToken(nextToken);
    if (nextUser) setUser(toDashboardUser(nextUser));
  }

  async function signOut() {
    const activeToken = token || localStorage.getItem(AUTH_TOKEN_KEY);
    
    // Feedback interaktif
    const { default: toast } = await import('react-hot-toast');
    const loadingToast = toast.loading('Logging out...');
    
    try {
      if (activeToken) await authLogout(activeToken).catch(() => undefined);
    } finally {
      localStorage.clear();
      sessionStorage.clear();
      setToken(null);
      setUser(null);
      
      toast.success('You have been signed out', { id: loadingToast });
      
      // Delay sebentar agar user sempat membaca toast
      setTimeout(() => {
        window.location.href = '/login';
      }, 800);
    }
  }

  useEffect(() => {
    const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!storedToken) {
      setLoading(false);
      return;
    }

    setToken(storedToken);
    getAuthMe(storedToken)
      .then((me) => setUser(toDashboardUser(me)))
      .catch(() => {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        setToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  return <AuthContext.Provider value={{ user, loading, token, setSession, signOut }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
